import React, { useState, useRef } from "react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import type { ApiError } from "@iziping/common";
import { useNotification } from "../Notification/useNotification";
import { ServerDownPage } from "../ServerDownPage/ServerDownPage";

interface ReactQueryProviderProps {
  logo: string;
  children: React.ReactNode;
}

export const ReactQueryProvider: React.FC<ReactQueryProviderProps> = ({
  logo,
  children,
}) => {
  const { notify } = useNotification();
  const [isServerDown, setIsServerDown] = useState(false);

  const handleError = (error: unknown) => {
    const apiError = error as ApiError;
    if (apiError?.code === "network.error") {
      setIsServerDown(true);
    } else if (
      apiError?.code === "forbidden" ||
      apiError?.code === "internal.server.error"
    ) {
      // 403 and 500 are handled by individual pages via isError
    } else if (apiError?.message) {
      notify(apiError.message, "error");
    }
  };

  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => handleErrorRef.current(error),
        }),
        mutationCache: new MutationCache({
          onError: (error) => handleErrorRef.current(error),
        }),
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  if (isServerDown) {
    return (
      <QueryClientProvider client={queryClient}>
        <ServerDownPage
          logo={logo}
          onRetry={() => {
            setIsServerDown(false);
            queryClient.clear();
          }}
        />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
